import { useParams } from 'react-router-dom'
// Superfície pública do paciente: pré-anamnese via link tokenizado, sem conta.
// Tudo passa pelas RPCs paciente_link_info / paciente_link_salvar (SECURITY DEFINER).
import { useCallback, useEffect, useState } from 'react'
import { CheckCircle2, Loader2, Save, Stethoscope } from 'lucide-react'
import { createClient } from '@/lib/supabase/client'
import { obrigatoriasFaltando, perguntaVisivel } from '@/lib/perguntas'
import type { Pergunta } from '@/lib/types'

interface FormInfo {
  paciente: string
  titulo: string
  status: 'pendente' | 'concluido' | 'dispensado'
  perguntas: Pergunta[]
  respostas: Record<string, unknown>
}

interface TermoInfo {
  paciente: string
  titulo: string
  versao: number
  conteudo: string
  comportamento: 'alertar' | 'aceite' | 'assinatura' | 'bloquear'
  status: string
}

export default function PaginaPacientePage() {
  const { token: linkToken } = useParams<{ token: string }>()
  const [info, setInfo] = useState<FormInfo | null>(null)
  const [respostas, setRespostas] = useState<Record<string, unknown>>({})
  const [erro, setErro] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [loading, setLoading] = useState(true)
  const [enviado, setEnviado] = useState(false)
  const [uploadBusy, setUploadBusy] = useState<string | null>(null)
  const [termo, setTermo] = useState<TermoInfo | null>(null)
  const [termoRespondido, setTermoRespondido] = useState<string | null>(null)
  const [nomeAssinante, setNomeAssinante] = useState('')

  const carregar = useCallback(async () => {
    if (!linkToken) return
    try {
      const supabase = createClient()
      const { data: escopoData } = await supabase.rpc('paciente_link_escopo', { p_token: linkToken })
      const escopo = (escopoData as string | null) ?? 'pre_anamnese'

      if (escopo === 'termo') {
        const { data, error } = await supabase.rpc('paciente_termo_info', { p_token: linkToken })
        if (error) throw error
        const t = data as unknown as TermoInfo
        setTermo(t)
        if (['aceito', 'assinado', 'recusado'].includes(t.status)) setTermoRespondido(t.status)
        return
      }

      const { data, error } = await supabase.rpc('paciente_link_info', { p_token: linkToken })
      if (error) throw error
      const i = data as unknown as FormInfo
      setInfo(i)
      setRespostas(i.respostas ?? {})
      if (i.status === 'concluido') setEnviado(true)
    } catch {
      setErro('Este link é inválido ou expirou. Fale com o consultório para receber um novo.')
    } finally {
      setLoading(false)
    }
  }, [linkToken])

  async function responderTermo(acao: 'aceitar' | 'assinar' | 'recusar') {
    if (!linkToken) return
    if (acao === 'assinar' && nomeAssinante.trim().split(' ').length < 2) {
      setErro('Digite seu nome completo para assinar.')
      return
    }
    setBusy(true)
    setErro(null)
    try {
      const supabase = createClient()
      const { error } = await supabase.rpc('paciente_termo_responder', {
        p_token: linkToken,
        p_acao: acao,
        p_nome_assinante: acao === 'assinar' ? nomeAssinante.trim() : null,
      })
      if (error) throw error
      setTermoRespondido(acao === 'recusar' ? 'recusado' : acao === 'assinar' ? 'assinado' : 'aceito')
    } catch {
      setErro('Não foi possível registrar. Tente novamente.')
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => { carregar() }, [carregar])

  if (!linkToken) return null

  function visivel(p: Pergunta): boolean {
    return perguntaVisivel(p, respostas)
  }

  function set(pid: string, valor: unknown) {
    setRespostas(prev => ({ ...prev, [pid]: valor }))
  }

  async function enviarArquivo(pid: string, file: File) {
    if (file.size > 25 * 1024 * 1024) { setErro('Arquivo acima de 25 MB.'); return }
    setUploadBusy(pid)
    setErro(null)
    try {
      const supabase = createClient()
      const seguro = file.name.replace(/[^\w.-]+/g, '_').slice(-80)
      const path = `uploads/${linkToken}/${crypto.randomUUID()}-${seguro}`
      const { error: eUp } = await supabase.storage.from('documentos').upload(path, file, {
        contentType: file.type || 'application/octet-stream',
      })
      if (eUp) throw eUp
      const { error: eRpc } = await supabase.rpc('paciente_link_anexar', {
        p_token: linkToken,
        p_storage_path: path,
        p_nome: file.name,
        p_content_type: file.type || null,
        p_tamanho: file.size,
        p_tipo: 'exame',
      })
      if (eRpc) throw eRpc
      set(pid, `📎 ${file.name}`)
    } catch {
      setErro('Não foi possível enviar o arquivo. Tente novamente.')
    } finally {
      setUploadBusy(null)
    }
  }

  async function salvar(finalizar: boolean) {
    if (!info) return
    if (finalizar) {
      const faltando = obrigatoriasFaltando(info.perguntas, respostas)
      if (faltando.length > 0) {
        setErro(`Faltam respostas obrigatórias: ${faltando.map(f => `"${f.rotulo}"`).join(', ')}`)
        return
      }
    }
    setBusy(true)
    setErro(null)
    setAviso(null)
    try {
      const supabase = createClient()
      const { error } = await supabase.rpc('paciente_link_salvar', {
        p_token: linkToken,
        p_respostas: respostas,
        p_finalizar: finalizar,
      })
      if (error) throw error
      if (finalizar) setEnviado(true)
      else setAviso('Progresso salvo — você pode voltar depois por este mesmo link.')
    } catch {
      setErro('Não foi possível salvar. Tente novamente.')
    } finally {
      setBusy(false)
    }
  }

  function Campo({ p }: { p: Pergunta }) {
    const v = respostas[p.id]
    switch (p.tipo) {
      case 'texto_longo':
        return <textarea className="field-input" rows={3} style={{ resize: 'vertical' }} value={String(v ?? '')} onChange={e => set(p.id, e.target.value)} />
      case 'numero':
        return <input type="number" inputMode="decimal" className="field-input" value={String(v ?? '')} onChange={e => set(p.id, e.target.value)} />
      case 'data':
        return <input type="date" className="field-input" value={String(v ?? '')} onChange={e => set(p.id, e.target.value)} />
      case 'sim_nao':
        return (
          <div style={{ display: 'flex', gap: 8 }} role="radiogroup" aria-label={p.rotulo}>
            {['sim', 'não'].map(op => (
              <button
                key={op} type="button" role="radio" aria-checked={v === op}
                className="badge"
                style={{
                  cursor: 'pointer', fontSize: 14, padding: '10px 22px', border: '1px solid', textTransform: 'capitalize',
                  background: v === op ? 'var(--primary-soft)' : 'var(--card)',
                  color: v === op ? 'var(--primary-deep)' : 'var(--text-secondary)',
                  borderColor: v === op ? 'var(--primary)' : 'var(--border-input)',
                }}
                onClick={() => set(p.id, op)}
              >
                {op}
              </button>
            ))}
          </div>
        )
      case 'selecao_unica':
        return (
          <select className="field-input" value={String(v ?? '')} onChange={e => set(p.id, e.target.value)}>
            <option value="">Escolha…</option>
            {(p.opcoes ?? []).map(op => <option key={op} value={op}>{op}</option>)}
          </select>
        )
      case 'multipla_selecao': {
        const arr = Array.isArray(v) ? (v as string[]) : []
        return (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {(p.opcoes ?? []).map(op => {
              const on = arr.includes(op)
              return (
                <button
                  key={op} type="button" aria-pressed={on}
                  className="badge"
                  style={{
                    cursor: 'pointer', fontSize: 13.5, padding: '9px 14px', border: '1px solid',
                    background: on ? 'var(--primary-soft)' : 'var(--card)',
                    color: on ? 'var(--primary-deep)' : 'var(--text-secondary)',
                    borderColor: on ? 'var(--primary)' : 'var(--border-input)',
                  }}
                  onClick={() => set(p.id, on ? arr.filter(x => x !== op) : [...arr, op])}
                >
                  {op}
                </button>
              )
            })}
          </div>
        )
      }
      case 'escala':
        return (
          <div>
            <input
              type="range" min={0} max={10} style={{ width: '100%' }}
              value={Number(v ?? 5)} onChange={e => set(p.id, Number(e.target.value))}
              aria-label={p.rotulo}
            />
            <div style={{ textAlign: 'center', fontWeight: 700, color: 'var(--primary-deep)' }}>{String(v ?? 5)}</div>
          </div>
        )
      case 'confirmacao':
        return (
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14.5 }}>
            <input type="checkbox" checked={v === true} onChange={e => set(p.id, e.target.checked)} style={{ width: 18, height: 18 }} />
            Confirmo
          </label>
        )
      case 'arquivo':
      case 'imagem':
      case 'documento': {
        const enviado = typeof v === 'string' && v.startsWith('📎')
        return (
          <div>
            {enviado ? (
              <div className="badge badge-ok" style={{ fontSize: 13 }}>{String(v)}</div>
            ) : (
              <input
                type="file"
                accept={p.tipo === 'imagem' ? 'image/*' : 'image/*,.pdf,.doc,.docx'}
                className="field-input"
                style={{ padding: 8 }}
                disabled={uploadBusy === p.id}
                onChange={e => {
                  const f = e.target.files?.[0]
                  if (f) enviarArquivo(p.id, f)
                }}
                aria-label={p.rotulo}
              />
            )}
            {uploadBusy === p.id && (
              <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>Enviando…</div>
            )}
          </div>
        )
      }
      case 'assinatura':
        return (
          <div className="badge badge-warn" style={{ fontSize: 13 }}>
            A assinatura digital chega na próxima atualização.
          </div>
        )
      default:
        return <input className="field-input" value={String(v ?? '')} onChange={e => set(p.id, e.target.value)} />
    }
  }

  return (
    <div className="public-screen" style={{ alignItems: 'flex-start', paddingTop: 28 }}>
      <div style={{ width: '100%', maxWidth: 560 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <div aria-hidden style={{ width: 38, height: 38, borderRadius: 10, background: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
            <Stethoscope size={19} />
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 16 }}>Médico 360</div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>Preparação da sua consulta</div>
          </div>
        </div>

        {loading ? (
          <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
            <Loader2 size={22} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />
            <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
          </div>
        ) : termo ? (
          termoRespondido ? (
            <div className="card" style={{ padding: 32, textAlign: 'center' }}>
              <CheckCircle2 size={34} aria-hidden style={{ color: termoRespondido === 'recusado' ? 'var(--warn)' : 'var(--ok)', marginBottom: 10 }} />
              <div style={{ fontWeight: 700, fontSize: 16 }}>
                {termoRespondido === 'recusado' ? 'Recusa registrada' : termoRespondido === 'assinado' ? 'Termo assinado' : 'Termo aceito'}, {termo.paciente}.
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
                {termoRespondido === 'recusado'
                  ? 'O consultório foi informado e vai falar com você.'
                  : 'Está tudo registrado com segurança. Até a consulta!'}
              </p>
            </div>
          ) : (
            <div className="card" style={{ padding: '22px 20px' }}>
              <h1 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 2px' }}>{termo.titulo}</h1>
              <p style={{ color: 'var(--muted)', fontSize: 12.5, margin: '0 0 14px' }}>
                Versão {termo.versao} · para {termo.paciente}
              </p>
              <div
                style={{
                  border: '1px solid var(--border)', borderRadius: 10, padding: 16,
                  maxHeight: 340, overflowY: 'auto', fontSize: 14, lineHeight: 1.6,
                  whiteSpace: 'pre-wrap', background: 'var(--bg)',
                }}
              >
                {termo.conteudo}
              </div>

              {(termo.comportamento === 'assinatura' || termo.comportamento === 'bloquear') && (
                <div style={{ marginTop: 14 }}>
                  <label className="field-label" htmlFor="assin">Seu nome completo (assinatura) *</label>
                  <input
                    id="assin" className="field-input" value={nomeAssinante}
                    onChange={e => setNomeAssinante(e.target.value)}
                    placeholder="Digite seu nome completo"
                    autoComplete="name"
                  />
                </div>
              )}

              {erro && <div className="field-error" role="alert" style={{ marginTop: 10 }}>{erro}</div>}

              <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
                {termo.comportamento === 'aceite' || termo.comportamento === 'alertar' ? (
                  <button className="btn-primary" style={{ flex: 1 }} disabled={busy} onClick={() => responderTermo('aceitar')}>
                    {busy && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />}
                    Li e aceito
                  </button>
                ) : (
                  <button className="btn-primary" style={{ flex: 1 }} disabled={busy} onClick={() => responderTermo('assinar')}>
                    {busy && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />}
                    Assinar termo
                  </button>
                )}
                <button className="btn-secondary" disabled={busy} onClick={() => responderTermo('recusar')}>
                  Recusar
                </button>
              </div>
            </div>
          )
        ) : !info ? (
          <div className="card" style={{ padding: 28, textAlign: 'center' }}>
            <div className="field-error" role="alert">{erro}</div>
          </div>
        ) : enviado ? (
          <div className="card" style={{ padding: 32, textAlign: 'center' }}>
            <CheckCircle2 size={34} aria-hidden style={{ color: 'var(--ok)', marginBottom: 10 }} />
            <div style={{ fontWeight: 700, fontSize: 16 }}>Tudo certo, {info.paciente}!</div>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
              Suas respostas foram enviadas ao consultório. Até a consulta!
            </p>
          </div>
        ) : (
          <div className="card" style={{ padding: '22px 20px' }}>
            <h1 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 2px' }}>Olá, {info.paciente}!</h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: '0 0 18px' }}>
              {info.titulo} — suas respostas ajudam a preparar sua consulta.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {info.perguntas.filter(visivel).map(p => (
                <div key={p.id}>
                  <label className="field-label" style={{ fontSize: 14 }}>
                    {p.rotulo}{p.obrigatoria && ' *'}
                  </label>
                  <Campo p={p} />
                </div>
              ))}
              {info.perguntas.length === 0 && (
                <div style={{ color: 'var(--muted)', fontSize: 14 }}>Este formulário não tem perguntas.</div>
              )}
            </div>

            {erro && <div className="field-error" role="alert" style={{ marginTop: 12 }}>{erro}</div>}
            {aviso && <div className="badge badge-ok" role="status" style={{ marginTop: 12 }}>{aviso}</div>}

            <div style={{ display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
              <button className="btn-primary" style={{ flex: 1 }} disabled={busy} onClick={() => salvar(true)}>
                {busy && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />}
                Enviar respostas
              </button>
              <button className="btn-secondary" disabled={busy} onClick={() => salvar(false)}>
                <Save size={15} aria-hidden /> Salvar e continuar depois
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
